import { finiteOrNull, mean, sd } from './stats';
import type { AbvScore, ScoredTrial, TlbsScores } from './types';

interface BiasPair { at: number; value: number; blockId: string; end: number }
/** Bidirectional nearest opposite pairs; ties choose the earlier trial, exact pairs deduplicate. */
export function tlbsSeries(valid: readonly ScoredTrial[], maxDistance = 5): BiasPair[] {
  const tn = valid.filter(t => t.trialType === 'threat-neutral').sort((a, b) => a.trialIndex - b.trialIndex);
  const pairs = new Map<string, BiasPair>();
  for (const from of tn) {
    let best: ScoredTrial | undefined, distance = Infinity;
    for (const to of tn) {
      if (from.blockId !== to.blockId || from.congruency === to.congruency) continue;
      const d = Math.abs(from.trialIndex - to.trialIndex);
      if (d <= maxDistance && d < distance) { best = to; distance = d; }
    }
    if (!best) continue;
    const it = from.congruency === 'incongruent' ? from : best;
    const ct = from.congruency === 'congruent' ? from : best;
    pairs.set(JSON.stringify([from.blockId, it.trialIndex, ct.trialIndex]), {
      at: Math.min(it.trialIndex, ct.trialIndex), end: Math.max(it.trialIndex, ct.trialIndex),
      blockId: from.blockId, value: (it.rt as number) - (ct.rt as number),
    });
  }
  return [...pairs.values()].sort((a, b) => a.blockId.localeCompare(b.blockId) || a.at - b.at || a.end - b.end);
}

/** App-specific MAD variant: sum absolute successive differences / number of within-block differences. */
export function computeTlbs(valid: readonly ScoredTrial[], maxDistance = 5): TlbsScores {
  const pairs = tlbsSeries(valid, maxDistance);
  const values = pairs.map(p => p.value), allRt = valid.map(t => t.rt as number);
  const pos = values.filter(x => x > 0), neg = values.filter(x => x < 0), succ: number[] = [];
  for (let k = 1; k < pairs.length; k += 1) {
    const a = pairs[k - 1]!, b = pairs[k]!;
    if (a.blockId === b.blockId) succ.push(Math.abs(b.value - a.value));
  }
  return {
    n: pairs.length, meanPositive: finiteOrNull(mean(pos)), meanNegative: finiteOrNull(mean(neg)),
    peakPositive: pos.length ? Math.max(...pos) : null, peakNegative: neg.length ? Math.min(...neg) : null,
    variability: finiteOrNull(mean(succ)),
    participantRtSd: finiteOrNull(sd(allRt)), participantRtMean: finiteOrNull(mean(allRt)),
  };
}

/**
 * Fixed bins of 20 ORIGINAL trial positions, retaining removed/neutral positions.
 * The final partial bin is retained. Missing either condition in any bin => undefined.
 * The raw sequence supplies block origins and completely removed bins.
 */
export function computeAbv(valid: readonly ScoredTrial[], binSize = 20, original: readonly ScoredTrial[] = valid): AbvScore {
  if (!Number.isInteger(binSize) || binSize < 1) throw new Error('binSize must be a positive integer');
  const origins = new Map<string, number>();
  for (const t of original) origins.set(t.blockId, Math.min(origins.get(t.blockId) ?? Infinity, t.trialIndex));
  const key = (t: ScoredTrial) => JSON.stringify([t.blockId, Math.floor((t.trialIndex - (origins.get(t.blockId) ?? 0)) / binSize)]);
  const groups = new Map<string, ScoredTrial[]>();
  for (const t of original) groups.set(key(t), []);
  const tn = valid.filter(t => t.trialType === 'threat-neutral');
  for (const t of tn) groups.get(key(t))?.push(t);
  const binScores: number[] = [];
  for (const bin of groups.values()) {
    const c = bin.filter(t => t.congruency === 'congruent').map(t => t.rt as number);
    const i = bin.filter(t => t.congruency === 'incongruent').map(t => t.rt as number);
    if (c.length && i.length) binScores.push(mean(i) - mean(c));
  }
  const m = mean(tn.map(t => t.rt as number));
  return { bins: binScores.length, expectedBins: groups.size,
    abv: binScores.length >= 2 && binScores.length === groups.size && m > 0 ? finiteOrNull(sd(binScores) / m) : null };
}
